import { Check } from "lucide-react";

const TREATMENT_GROUPS = [
  {
    n: "01",
    title: "Preventive & Routine Care",
    items: [
      { name: "Dental Check-ups & X-rays", note: "Diagnostics" },
      { name: "Cleaning (Scaling & Root Planing)", note: "Removal of plaque and tartar" },
      { name: "Fluoride Treatments", note: "Strengthening tooth enamel" },
      { name: "Dental Sealants", note: "Protective coating to prevent decay" },
    ],
  },
  {
    n: "02",
    title: "Restorative Treatments",
    sub: "Fixing damaged teeth",
    items: [
      { name: "Composite Tooth Fillings", note: "Repairing cavities" },
      { name: "Root Canal Treatment", note: "Treating infected, deep tooth decay" },
      { name: "Crowns", note: "Caps placed over damaged teeth" },
      { name: "Bridges", note: "Replacing missing teeth" },
      { name: "Dentures", note: "Removable appliances for missing teeth" },
      { name: "Inlays & Onlays", note: "Indirect fillings for large cavities" },
    ],
  },
  {
    n: "03",
    title: "Cosmetic Dentistry",
    sub: "Enhancing appearance",
    items: [
      { name: "Teeth Whitening", note: "Brightening stains and discoloration" },
      { name: "Veneers", note: "Thin covers placed over teeth" },
      { name: "Dental Bonding", note: "Tooth-coloured resin to repair imperfections" },
      { name: "Smile Makeover", note: "Combination of procedures to improve aesthetics" },
    ],
  },
  {
    n: "04",
    title: "Orthodontics",
    sub: "Straightening teeth",
    items: [
      { name: "Braces", note: "Traditional wire methods for alignment" },
      { name: "Invisible Braces / Aligners", note: "Clear, removable aligners (e.g. Invisalign)" },
    ],
  },
  {
    n: "05",
    title: "Surgical & Specialised Procedures",
    items: [
      { name: "Tooth Extractions", note: "Removal of damaged or unwanted teeth" },
      { name: "Wisdom Tooth Removal", note: "Specialised extraction of the last molars" },
      { name: "Dental Implants", note: "Permanent replacement of missing teeth" },
      { name: "Gum Surgery / Periodontics", note: "Treating gum diseases and receding gums" },
      { name: "Laser Dentistry", note: "Procedures for gum contouring or cleaning" },
    ],
  },
];

export default function TreatmentMenu() {
  return (
    <section
      id="treatments"
      className="py-20 md:py-28 bg-brand-bg"
      data-testid="treatment-menu"
    >
      <div className="container-site">
        <div className="max-w-3xl mb-14">
          <p className="overline mb-3">Complete treatment menu</p>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-pine leading-tight">
            Every treatment we offer, <span className="italic text-brand-clay">at a glance.</span>
          </h2>
          <p className="mt-5 text-brand-textMuted leading-relaxed">
            From a routine cleaning to a full smile makeover — here is the
            complete list of dental treatments we deliver at home in Pune.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TREATMENT_GROUPS.map((g, i) => (
            <article
              key={g.title}
              data-testid={`treatment-group-${i}`}
              className={`bg-white rounded-2xl border border-brand-line/60 p-7 md:p-8 hover:shadow-lg transition-shadow ${
                i === TREATMENT_GROUPS.length - 1 && TREATMENT_GROUPS.length % 2 === 1
                  ? "md:col-span-2"
                  : ""
              }`}
            >
              <div className="flex items-baseline gap-3 mb-5">
                <span className="font-serif text-3xl text-brand-clay">{g.n}</span>
                <div>
                  <h3 className="font-serif text-2xl md:text-[28px] text-brand-pine leading-tight">
                    {g.title}
                  </h3>
                  {g.sub ? (
                    <p className="text-xs uppercase tracking-[0.18em] text-brand-textMuted mt-1">
                      {g.sub}
                    </p>
                  ) : null}
                </div>
              </div>

              <ul className="space-y-3 mt-2">
                {g.items.map((item, j) => (
                  <li
                    key={item.name}
                    data-testid={`treatment-item-${i}-${j}`}
                    className="flex gap-3"
                  >
                    <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-brand-clay/10 text-brand-clay flex items-center justify-center">
                      <Check size={12} strokeWidth={2.5} />
                    </span>
                    <span className="leading-snug">
                      <span className="font-medium text-brand-pine">{item.name}</span>
                      <span className="text-brand-textMuted">: {item.note}.</span>
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href="#booking" className="btn-primary" data-testid="treatment-menu-cta">
            Not sure which one you need? Talk to a dentist
          </a>
        </div>
      </div>
    </section>
  );
}
